import { ReactNode } from "react";
import { Alert, Box, Paper, Stack, Typography } from "@mui/material";
import { ClientBillingInvoice, ClientBillingSubscription } from "../../types/billing";
import BillingInvoicesTable from "./BillingInvoicesTable";
import BillingSubscriptionCard from "./BillingSubscriptionCard";

// Clears the sticky app header and any banner stacked above it.
const STICKY_TOP = "calc(var(--app-banner-height) + 88px)";

interface Props {
  subscriptions: (ClientBillingSubscription & { id?: string })[];
  invoices: ClientBillingInvoice[];
  pane: ReactNode;
}

export default function BillingOverview({ subscriptions, invoices, pane }: Props) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 3,
        alignItems: "start",
        gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "300px minmax(0, 1fr)" },
      }}
    >
      <Box
        sx={{
          position: { md: "sticky" },
          top: { md: STICKY_TOP },
          maxHeight: { md: `calc(100vh - ${STICKY_TOP} - 16px)` },
          overflowY: { md: "auto" },
        }}
      >
        {pane}
      </Box>

      <Stack spacing={3} sx={{ minWidth: 0 }}>
        {subscriptions.length === 0 && (
          <Alert severity="error" sx={{ fontWeight: 500 }}>
            No subscriptions. This billing profile has a Stripe customer but no subscription.
          </Alert>
        )}
        {subscriptions.map((subscription, idx) => (
          <BillingSubscriptionCard key={subscription.id ?? idx} subscription={subscription} />
        ))}
        <Paper variant="outlined" component="section" aria-label="Last invoices" sx={{ p: 2.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Last invoices</Typography>
          {invoices.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No invoices yet.</Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <BillingInvoicesTable invoices={invoices} />
            </Box>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
